import { Component, Input, Output, OnInit, QueryList, ViewChildren, ElementRef, EventEmitter, HostListener } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Observable } from 'rxjs/internal/Observable';
import { GlobalService } from '../../_services/global.service';
import { LoggerService } from '../../_services/logger.service';
import { startWith, map } from 'rxjs/operators';

@Component({
  selector: 'app-product-form',
  templateUrl: './product-form.component.html',
  styleUrls: ['./product-form.component.scss', '../detalhe.component.scss']
})
export class ProductFormComponent implements OnInit {

  @Input() produtoForm!: FormArray;
  @Input() doShortcutActive: boolean = true;
  
  @Output() validarEntrada = new EventEmitter<void>();

  // refs para inputs e autocompletes
  @ViewChildren('produtoInput') produtoInputs!: QueryList<ElementRef>;
  @ViewChildren(MatAutocompleteTrigger) autocompleteTriggers!: QueryList<MatAutocompleteTrigger>;

  private addProdutoNextBlocked = false;

  public produtoFiltrado: string[] = [];
  public posologiaFiltrado: string[] = [];

  public filteredProdutos$: Observable<string[]>[] = [];
  public filteredPosologia$: Observable<string[]>[] = [];

  constructor(
    private fb: FormBuilder,
    private globalService: GlobalService,
    private logger: LoggerService
  ) {}

  ngOnInit(): void {
    if (this.produtoForm.length === 0) {
      this.produtoForm.push(this.createProduto());
    }
    this.setupProdutoFilters();
  }

  // Getter para o FormArray
  get produtos(): FormArray {
    return this.produtoForm as FormArray;
  }

  private createProduto(nome: string = '', posologia: string = ''): FormGroup {
    return this.fb.group({
      nome: [nome, Validators.required],
      posologia: [posologia]
    });
  }

  addProdutoFirst(): void {
    if (this.addProdutoNextBlocked) return;
    this.addProdutoNextBlocked = true;

    this.produtos.insert(0, this.createProduto());

    setTimeout(() => (this.addProdutoNextBlocked = false), 300);
  }

  addProdutoNext(index: number): void {
    if (this.addProdutoNextBlocked) return;
    this.addProdutoNextBlocked = true;

    const insertIndex = Math.min(index + 1, this.produtos.length);
    this.produtos.insert(insertIndex, this.createProduto());

    setTimeout(() => {
      this.autocompleteTriggers.toArray().forEach(t => t.closePanel());
      this.produtoInputs.toArray()[insertIndex]?.nativeElement.focus();
      this.addProdutoNextBlocked = false;
    }, 300);
  }

  removeProduto(index: number): void {
    if (this.produtos.length > 1) {
      this.produtos.removeAt(index);
    }
  }

  clearProdutos(): void {
    this.produtos.clear();
    this.produtos.push(this.createProduto());

    setTimeout(() => this.produtoInputs.toArray()[0]?.nativeElement.focus(), 300);
  }
  
  verificarAtalho(event: KeyboardEvent, campo: string) {
    const campoToNumber = parseInt(campo, 10);

    if (event.altKey && (event.key === 'q' || event.key === 'Q')) {
      if (this.produtos.length > 1) {
        this.removeProduto(campoToNumber);
      }
    }

    if (event.altKey && (event.key === 'w' || event.key === 'W')) {
      this.addProdutoNext(campoToNumber);
    }
  }
  
  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (!event.altKey) return;
    event.preventDefault();
    if (!this.doShortcutActive) return;
    switch (event.key) {
      case 'F': case 'f': this.clearProdutos(); break;
    }
  }
  
  isProdutoFieldDirty(index: number, field: string): boolean {
    const group = this.produtos.at(index) as FormGroup;
    return group.get(field)?.dirty ?? false;
  }

  private setupProdutoFilters(): void {
    this.filteredProdutos$ = [];
    this.filteredPosologia$ = [];
    this.produtos.controls.forEach((produtoGroup: FormGroup, index: number) => {
      const nomeControl = produtoGroup.get('nome');
      const posologiaControl = produtoGroup.get('posologia');

      if (nomeControl) {
        this.filteredProdutos$[index] = nomeControl.valueChanges.pipe(
          startWith(''),
          map(val => this.filterListaProdutos(val))
        );
      }
      if (posologiaControl) {
        this.filteredPosologia$[index] = posologiaControl.valueChanges.pipe(
          startWith(''),
          map(val => this.filterListaPosologia(val))
        );
      }
    });
  }

  private filterListaProdutos(val: string): string[] {
    const filterValue = val ? val.toUpperCase() : '';
    return this.globalService.listaProdutos.filter(produto =>
      produto.toUpperCase().includes(filterValue)
    );
  }

  private filterListaPosologia(val: string): string[] {
    const filterValue = val ? val.toUpperCase() : '';
    return this.globalService.listaPosologia.filter(posologia =>
      posologia.toUpperCase().includes(filterValue)
    );
  }
}
